/**
 * NeutralizationBurst — animated visual cue that fires when a violation is
 * neutralized.
 *
 * Wraps arbitrary children and overlays a radial burst (one expanding ring
 * plus a brief flash) while `active` is true. The burst is purely
 * decorative — interaction with the wrapped children is preserved (pointer
 * events on the burst layer are disabled).
 *
 * When `active` stays true across successive events (e.g., the consumer
 * holds the last result on screen), pass a changing `triggerKey` to replay
 * the burst on each new event. Each fresh `triggerKey` mounts a new
 * overlay under `AnimatePresence`, so the animation runs again.
 *
 * Color defaults to the SAFE green from `tokens.data.json`, or to the
 * specified theorem's accent color when `theorem` is provided — so a burst
 * over a SEPARATE result reads as SEPARATE, a burst over PERMIT reads as
 * PERMIT, etc.
 *
 * Respects `prefers-reduced-motion`: when the user has reduced motion
 * enabled, the burst is suppressed entirely and only the children render.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { SAFE, THEOREM_COLOR, type CanonicalTheoremName } from "../design-system/tokens.js";
import {
  burstFlashVariants,
  burstRingVariants,
  motionDurations,
  motionEasings,
} from "../styling/motionTokens.js";

export interface NeutralizationBurstProps {
  /** When true, the burst overlay is rendered. */
  readonly active: boolean;
  /**
   * Identity of the current burst event. Each new value (compared via
   * `Object.is`) replays the animation while `active` is true. Pass a
   * counter that increments per event, or any unique token derived from
   * the triggering result (e.g. a result id, timestamp, or message hash).
   */
  readonly triggerKey?: string | number;
  /** Optional canonical theorem name — tints the burst with that theorem's accent. */
  readonly theorem?: CanonicalTheoremName;
  /** Optional override color. Wins over `theorem` if both are set. */
  readonly color?: string;
  /** Children render underneath the burst overlay. */
  readonly children?: React.ReactNode;
}

export const NeutralizationBurst = React.memo(function NeutralizationBurst({
  active,
  triggerKey,
  theorem,
  color,
  children,
}: NeutralizationBurstProps): React.JSX.Element {
  const prefersReducedMotion = useReducedMotion() ?? false;
  const tint = color ?? (theorem !== undefined ? THEOREM_COLOR[theorem] : SAFE[500]);
  const overlayKey = triggerKey ?? "neutralization-burst";

  return (
    <div style={{ position: "relative" }}>
      {children}
      <AnimatePresence>
        {active && !prefersReducedMotion && (
          <motion.div
            key={overlayKey}
            data-testid="neutralization-burst-overlay"
            data-trigger-key={String(overlayKey)}
            aria-hidden="true"
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: motionDurations.burst,
              ease: motionEasings.ringExpand,
            }}
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              borderRadius: "0.5rem",
            }}
          >
            <motion.span
              variants={burstRingVariants}
              initial="initial"
              animate="expand"
              transition={{
                duration: motionDurations.burst,
                ease: motionEasings.ringExpand,
              }}
              style={{
                width: "8rem",
                height: "8rem",
                borderRadius: "9999px",
                border: `2px solid ${tint}`,
                boxShadow: `0 0 24px ${tint}66`,
              }}
            />
            <motion.span
              variants={burstFlashVariants}
              initial="initial"
              animate="fade"
              transition={{
                duration: motionDurations.flash,
                ease: motionEasings.ringExpand,
              }}
              style={{
                position: "absolute",
                inset: 0,
                background: `radial-gradient(circle at center, ${tint}33 0%, transparent 60%)`,
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});
